import st from "@/components/pages/blog/ai-article/common.module.scss";
import s from "./ArticleCategories.module.scss";
import { Modal, ModalProps } from "./Modal";
import { InputSearch } from "./InputSearch";
import React, { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import cn from "classnames";
import { Link } from "@/components/pages/blog/ai-article/components/Link";
import { highlight } from "@/helpers/highlight";

type SearchResult = {
  title: string;
  slug: string;
  articles: {
    title: string;
    slug: string;
  }[];
};

export type ArticleCategoriesModalProps = Omit<ModalProps, "children"> & {
  selectedIdCategory?: number;
  onClose?: () => void;
};

export const ArticleCategoriesModal = ({
  selectedIdCategory,
  onClose,
  ...props
}: ArticleCategoriesModalProps) => {
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitiated, setIsInitiated] = useState(false);
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");

  const [results, setResults] = useState<SearchResult[]>([]);

  const handleSearch = (value: string) => setSearchTerm(value);

  const submitSearch = async (searchTerm: string) => {
    const term = searchTerm.trim();
    if (!term) return;

    setLoading(true);
    try {
      const requestUrl = selectedIdCategory
        ? `/api/articles?key=title&idCategory=${selectedIdCategory}&title=${term}`
        : `/api/articles?key=title&title=${term}`;
      const resp = await (
        await fetch(requestUrl, {
          cache: "no-store",
        })
      ).json();

      const data = resp.data.reduce((prev, next) => {
        const category = next.attributes.categories.data?.[0];

        if (!category) return prev;
        const articleInfo = {
          slug: next.attributes.slug,
          title: next.attributes.sidebarTitle || next.attributes.heading,
          displayOrder: next.attributes.displayOrder,
        };
        const categoryIndex = prev.findIndex(({ slug }) => slug === category.attributes.slug);
        if (categoryIndex > -1) {
          prev[categoryIndex].articles.push(articleInfo);
          return prev;
        } else {
          prev.push({
            slug: category.attributes.slug,
            title: category.attributes.sidebarTitle || category.attributes.heading,
            displayOrder: category.attributes.displayOrder,
            articles: [articleInfo],
          });

          return prev;
        }
      }, []);

      data.sort((a, b) => {
        a.articles.sort((c, d) => c.displayOrder - d.displayOrder);
        b.articles.sort((c, d) => c.displayOrder - d.displayOrder);
        return a.displayOrder - b.displayOrder;
      });

      setResults(data);
      setIsInitiated(true);
      setSubmittedSearchTerm(term);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        submitSearch(searchTerm);
      }, 400),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  const handleClose = () => {
    setSearchTerm("");
    setLoading(false);
    setResults([]);
    setSubmittedSearchTerm("");
    setIsInitiated(false);
    onClose?.();
  };

  const totalCount = results.reduce((prev, { articles }) => prev + articles.length, 0);

  return (
    <Modal
      {...props}
      className={cn(props.className, {
        [s.loading]: isLoading,
      })}
      onClose={handleClose}
    >
      <div className={s.modalSearchHeading}>
        <strong className={s.modalHeading}>Search in all articles</strong>
        <InputSearch
          placeholder="Search"
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          number={totalCount}
          className={s.searchInput}
          autoFocus
        />
      </div>
      <div className={s.modalContent}>
        {!isLoading && searchTerm && !results.length && isInitiated && (
          <div className={cn(st.fBig, s.notFound)}>
            Sorry, we couldn&apos;t find any results for <strong>{submittedSearchTerm}</strong>.<br />
            Please try a different query instead.
          </div>
        )}
        {results.map(({ title, slug, articles }) => (
          <div key={slug} className={s.modalContentGroup}>
            <strong className={s.modalHeading}>
              {title} ({articles.length})
            </strong>
            <ul>
              {articles.map((article) => (
                <li key={article.slug}>
                  <Link
                    href={`/${slug}/${article.slug}`}
                    className={st.h4}
                    dangerouslySetInnerHTML={{
                      __html: highlight(article.title, searchTerm.trim(), s.resultHighlighted),
                    }}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Modal>
  );
};
